import Header from "./components/header"
import Footer from "./components/footer"
import { Helmet } from "react-helmet"
import { motion } from "framer-motion"
import { useInView } from "react-intersection-observer"

export default function AboutFormula() {
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1
    });

    return (
        <>
            <Helmet>
                <title>iFLO Pro | About Formula - Professional HVAC Cleaning Solution</title>
                <meta name="description" content="The Pro formula is our most concentrated and powerful formula made only for professional use. Learn about our professional-grade HVAC cleaning solution with maximum concentration and safety standards." />
                <meta name="keywords" content="HVAC cleaning, water damage prevention, professional formula, HVAC maintenance, bacteria prevention, drain cleaning, insurance claims, HVAC safety" />
                <meta name="robots" content="index, follow" />
                <meta property="og:title" content="iFLO Pro | About Formula - Professional HVAC Cleaning Solution" />
                <meta property="og:description" content="Discover our professional-grade HVAC cleaning formula with maximum concentration and safety standards." />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="/formula.png" />
                <meta property="og:url" content="https://iflopro.com/about-formula" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="iFLO Pro | About Formula - Professional HVAC Cleaning Solution" />
                <meta name="twitter:description" content="Discover our professional-grade HVAC cleaning formula with maximum concentration and safety standards." />
                <meta name="twitter:image" content="/formula.png" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Product",
                        "name": "iFLO Pro Formula",
                        "description": "Professional-grade HVAC cleaning formula with maximum concentration and safety standards",
                        "brand": {
                            "@type": "Brand",
                            "name": "iFLO Pro"
                        },
                        "image": "/formula.png",
                    })}
                </script>
            </Helmet>
            <Header />

            <main className="bg-gradient-to-b from-white to-gray-50">
                {/* Hero Section */}
                <section className="min-h-screen flex items-center justify-center px-4 py-20">
                    <div className="max-w-7xl mx-auto">
                        <motion.div 
                            ref={ref}
                            initial={{ opacity: 0, y: 20 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.8 }}
                            className="grid md:grid-cols-2 gap-12 items-center"
                        >
                            {/* Image Section */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={inView ? { opacity: 1, scale: 1 } : {}}
                                transition={{ duration: 0.8, delay: 0.2 }}
                                className="relative"
                            >
                                <div className="relative z-10">
                                    <img
                                        src="./formula.png"
                                        alt="iFLO Pro Bottle"
                                        className="w-full max-w-lg mx-auto drop-shadow-2xl"
                                    />
                                </div>
                                <motion.div
                                    className="absolute inset-0 bg-yellow-400 rounded-full filter blur-3xl opacity-20"
                                    animate={{
                                        scale: [1, 1.2, 1],
                                        opacity: [0.2, 0.3, 0.2],
                                    }}
                                    transition={{
                                        duration: 4,
                                        repeat: Infinity,
                                        ease: "easeInOut",
                                    }}
                                />
                            </motion.div>

                            {/* Content Section */}
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                animate={inView ? { opacity: 1, x: 0 } : {}}
                                transition={{ duration: 0.8, delay: 0.4 }}
                                className="space-y-6"
                            >
                                <motion.h1 
                                    className="text-5xl font-bold text-gray-900"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={inView ? { opacity: 1, y: 0 } : {}}
                                    transition={{ duration: 0.8, delay: 0.6 }}
                                >
                                    iFLO Pro Formula
                                </motion.h1>
                                
                                <motion.p 
                                    className="text-xl text-gray-600 leading-relaxed"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={inView ? { opacity: 1, y: 0 } : {}}
                                    transition={{ duration: 0.8, delay: 0.8 }}
                                >
                                    The Pro formula is our most concentrated and powerful formula made
                                    only for professional use. View SDS and ingredient information.
                                </motion.p>

                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={inView ? { opacity: 1, y: 0 } : {}}
                                    transition={{ duration: 0.8, delay: 1 }}
                                >
                                    <a
                                        href="/ingredients"
                                        className="inline-flex items-center px-8 py-4 bg-yellow-500 text-black font-semibold rounded-full hover:bg-yellow-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                                    >
                                        Read More
                                        <svg 
                                            className="w-5 h-5 ml-2" 
                                            fill="none" 
                                            stroke="currentColor" 
                                            viewBox="0 0 24 24"
                                        >
                                            <path 
                                                strokeLinecap="round" 
                                                strokeLinejoin="round" 
                                                strokeWidth={2} 
                                                d="M9 5l7 7-7 7" 
                                            />
                                        </svg>
                                    </a>
                                </motion.div>

                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={inView ? { opacity: 1, y: 0 } : {}}
                                    transition={{ duration: 0.8, delay: 1.2 }}
                                    className="mt-6 bg-black rounded-lg shadow-md shadow-yellow-500 items-center justify-center"
                                >
                                    <img 
                                        src="/Insurance/zooglea.webp" 
                                        alt="iFLO Pro Formula" 
                                        className="rounded-lg w-full max-w-md object-contain mx-auto"
                                    />
                                </motion.div>
                            </motion.div>
                        </motion.div>
                    </div>
                </section>

                {/* Features Section */}
                <section className="py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-4">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="grid md:grid-cols-3 gap-8"
                        >
                            {[
                                {
                                    title: "Professional Grade",
                                    description: "Specially formulated for HVAC professionals",
                                    icon: "⚡"
                                },
                                {
                                    title: "Maximum Concentration",
                                    description: "Our most powerful formula for optimal results",
                                    icon: "💪"
                                },
                                {
                                    title: "Safety First",
                                    description: "Compliant with industry safety standards",
                                    icon: "🛡️"
                                }
                            ].map((feature, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.8, delay: index * 0.2 }}
                                    className="p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
                                >
                                    <div className="text-4xl mb-4">{feature.icon}</div>
                                    <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                                    <p className="text-gray-600">{feature.description}</p>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </section>
            </main>

            <Footer />
        </>
    )
}
